// Ekselens Consulting — services data (12 services).
// Used by Home grid, Services landing groups, and dedicated service pages.

export const services = [
  {
    slug: "ai-readiness-and-adoption",
    name: "AI Readiness and Adoption",
    short: "Identify safe, practical and measurable AI opportunities, then build the governance, data, risk and adoption foundations to pilot and scale responsibly.",
    group: "strategy",
    hero: "Move from AI curiosity to safe, practical and measurable adoption.",
    intro: "AI can improve productivity, service quality, knowledge management, decision support and internal operations. But successful adoption requires more than access to tools. Organisations need clear use cases, suitable data, responsible governance, workforce confidence, human oversight and measurable benefits.",
    challenges: [
      "AI experimentation is happening without consistent controls.",
      "Leaders want productivity gains but lack clear use cases.",
      "Staff are unsure how to use AI safely and effectively.",
      "Data quality or access may limit AI opportunities.",
      "Governance, risk and accountability are unclear.",
      "AI benefits are not being measured.",
    ],
    howWeHelp:
      "We help leadership teams move past experimentation. Ekselens builds the readiness foundations — governance, data, use cases, human oversight, workforce confidence — so AI moves from curiosity to outcomes you can evidence.",
    delivers: [
      "AI readiness assessment",
      "AI use case longlist and prioritisation matrix",
      "AI risk assessment",
      "Responsible AI governance model",
      "Human-in-the-loop process design",
      "AI pilot roadmap",
      "Workforce adoption and training plan",
      "AI benefits and adoption dashboard",
    ],
    outputs: [
      "Executive-ready readiness scorecard",
      "Prioritised pilot portfolio with measurable success criteria",
      "Responsible AI policy and governance pack",
      "Adoption and capability roadmap",
    ],
    whyItMatters:
      "AI investment without readiness creates risk and disappointment. Ekselens helps you adopt AI in ways your board, regulator, customers and workforce can trust.",
    related: ["digital-and-technology-transformation", "data-insight-and-performance", "benefits-realisation-and-assurance"],
  },
  {
    slug: "digital-and-technology-transformation",
    name: "Digital and Technology Transformation",
    short: "Assess digital maturity, identify technology-enabled opportunities and create practical transformation roadmaps grounded in business outcomes.",
    group: "strategy",
    hero: "Turn digital ambition into practical transformation.",
    intro: "Digital transformation is most effective when it improves real services, simplifies processes, supports better decisions and creates measurable value. Ekselens helps organisations assess digital maturity, identify technology-enabled opportunities and build practical roadmaps grounded in business outcomes.",
    challenges: [
      "Digital activity is fragmented or poorly prioritised.",
      "Technology changes are not linked to service improvement.",
      "Processes remain manual or inefficient.",
      "Data and reporting do not support decisions.",
      "Transformation plans lack ownership or sequencing.",
      "Benefits are unclear or not measured.",
    ],
    howWeHelp:
      "We connect digital ambition to operational reality. Ekselens diagnoses current maturity, prioritises technology-enabled opportunities and sequences them into a roadmap your delivery teams can mobilise against.",
    delivers: [
      "Digital maturity assessment",
      "Current-state review",
      "Digital opportunity portfolio",
      "Transformation roadmap",
      "Data and insight improvement actions",
      "Business case and benefits logic",
      "Governance and delivery plan",
    ],
    outputs: [
      "Maturity heatmap across digital domains",
      "Sequenced transformation roadmap",
      "Investment-ready business case",
      "Delivery governance pack",
    ],
    whyItMatters:
      "Boards approve transformation programmes that show clear sequencing, evidence and benefits logic — not slideware.",
    related: ["ai-readiness-and-adoption", "service-and-operating-model-transformation", "data-insight-and-performance"],
  },
  {
    slug: "project-programme-and-pmo-improvement",
    name: "Project, Programme and PMO Improvement",
    short: "Improve governance, reporting, delivery controls, RAID discipline, PMO effectiveness and executive confidence in delivery.",
    group: "delivery",
    hero: "Improve delivery confidence, governance and project outcomes.",
    intro: "Projects and programmes fail when scope is unclear, governance is weak, reporting is optimistic, dependencies are unmanaged and benefits are not owned. Ekselens helps organisations strengthen project delivery discipline, improve PMO effectiveness and build executive confidence in delivery.",
    challenges: [
      "Steering committees lack a clear, honest view of delivery.",
      "Reporting is inconsistent, optimistic or backwards-looking.",
      "RAID discipline is weak; dependencies are not actively managed.",
      "PMO activity is administrative rather than decision-supporting.",
      "Benefits are written into the business case but not tracked.",
      "Delivery teams are stretched across too many priorities.",
    ],
    howWeHelp:
      "We strengthen the disciplines that make portfolios deliverable: governance that drives decisions, reporting that surfaces truth, controls that protect outcomes, and PMOs that earn their seat at the executive table.",
    delivers: [
      "Project and programme maturity assessment",
      "PMO assessment and improvement roadmap",
      "Governance model and terms of reference",
      "RAID, dependency, decision and action controls",
      "Executive dashboard and steering pack",
      "Delivery assurance review",
      "Benefits tracking approach",
      "PMO setup or improvement plan",
    ],
    outputs: [
      "Governance and reporting blueprint",
      "Executive delivery dashboard",
      "Assurance review report and recommendations",
      "PMO operating model",
    ],
    whyItMatters:
      "Senior leaders need confidence that delivery is honest, controlled and recoverable when it slips.",
    related: ["business-change-and-adoption", "benefits-realisation-and-assurance", "governance-and-delivery-control"],
  },
  {
    slug: "business-change-and-adoption",
    name: "Business Change and Adoption",
    short: "Engage stakeholders, assess impacts, prepare users, manage readiness and support adoption of new ways of working.",
    group: "adoption",
    hero: "Help people adopt change, not just receive it.",
    intro: "Transformation succeeds when people understand the change, are prepared for what it means, have the skills to adopt it and are supported after implementation. Ekselens helps organisations manage stakeholder engagement, change impact, communications, training, readiness and adoption.",
    challenges: [
      "Communication is mistaken for change management.",
      "Impacts on roles, processes and ways of working are not understood.",
      "Training is generic rather than tailored to the change.",
      "Readiness is assumed; adoption is not measured.",
      "Hypercare is short or ends before adoption embeds.",
      "Change fatigue is real and unmanaged.",
    ],
    howWeHelp:
      "We make adoption a design discipline. From stakeholder mapping to hypercare exit, Ekselens builds the structured approach that turns delivery outputs into ways of working people actually use.",
    delivers: [
      "Change strategy",
      "Stakeholder map and engagement plan",
      "Change impact assessment",
      "Communications plan",
      "Training needs analysis",
      "Readiness assessment",
      "Change champion network plan",
      "Hypercare and adoption support",
      "Adoption measurement framework",
    ],
    outputs: [
      "Change strategy and impact pack",
      "Stakeholder engagement plan",
      "Readiness dashboard",
      "Adoption measurement framework",
    ],
    whyItMatters:
      "Technology projects fail at adoption far more often than at build. Adoption is where benefits are earned.",
    related: ["capability-and-training-needs-analysis", "stakeholder-engagement-and-communications", "service-and-operating-model-transformation"],
  },
  {
    slug: "service-and-operating-model-transformation",
    name: "Service and Operating Model Transformation",
    short: "Redesign services, processes, roles, governance, data, technology and performance models to improve operational effectiveness.",
    group: "strategy",
    hero: "Design services and operating models that work in practice.",
    intro: "Operating model change is more than structure. It requires clarity across services, processes, roles, governance, technology, data, suppliers, performance and capability. Ekselens helps organisations redesign how work gets done so that services are clearer, faster and easier to manage.",
    challenges: [
      "Services overlap, are duplicated or have no clear owner.",
      "Processes are tribal, undocumented or inconsistent.",
      "Roles and decision rights are unclear.",
      "Performance is not measured against service outcomes.",
      "Technology and data don't support the model.",
      "Restructures replace boxes without changing how work is done.",
    ],
    howWeHelp:
      "We design operating models from the service outwards. Ekselens combines service definition, process redesign, RACI, governance and performance frameworks into a target model your organisation can transition to.",
    delivers: [
      "Current-state operating model assessment",
      "Service redesign workshops",
      "Target operating model design",
      "RACI and decision rights",
      "Future process model",
      "Service KPI framework",
      "Transition roadmap",
      "Change impact assessment",
    ],
    outputs: [
      "Target operating model blueprint",
      "Service catalogue and KPIs",
      "Transition roadmap and impact pack",
      "Governance and decision rights model",
    ],
    whyItMatters:
      "Sustainable transformation needs an operating model — not just a project. We design models that survive the consultants leaving.",
    related: ["it-service-management-and-operational-excellence", "business-change-and-adoption", "supplier-and-technology-value-optimisation"],
  },
  {
    slug: "supplier-and-technology-value-optimisation",
    name: "Supplier and Technology Value Optimisation",
    short: "Improve supplier visibility, contract control, renewal planning, service performance, SaaS value and technology portfolio decisions.",
    group: "optimisation",
    hero: "Improve supplier control, renewal visibility and technology value.",
    intro: "Many organisations carry avoidable supplier risk, unclear contract ownership, missed renewal opportunities, unused licences and technology duplication. Ekselens helps clients create visibility, reduce risk and identify value opportunities across suppliers, contracts, SaaS, licences and technology portfolios.",
    challenges: [
      "No single view of supplier contracts, owners or renewal dates.",
      "Renewals happen by surprise, not by negotiation.",
      "SaaS licences are over-bought or under-used.",
      "Performance scorecards exist but don't drive action.",
      "Contract risk is hidden in legacy paperwork.",
      "Technology portfolio decisions lack a value lens.",
    ],
    howWeHelp:
      "We bring suppliers and technology into one visible portfolio. Ekselens builds the inventories, scorecards and renewal mechanics that turn supplier and technology spend into a managed value lever.",
    delivers: [
      "Supplier contract inventory",
      "Renewal planner and notice-date heatmap",
      "Supplier performance scorecard",
      "Contract risk assessment",
      "Licence utilisation review",
      "Supplier governance model",
      "Cost and optimisation opportunity register",
      "Technology portfolio rationalisation roadmap",
    ],
    outputs: [
      "Supplier and contract inventory",
      "Renewal heatmap with negotiation windows",
      "Optimisation opportunity register",
      "Portfolio rationalisation roadmap",
    ],
    whyItMatters:
      "Supplier and technology value is the easiest savings story most CFOs are missing — and one of the easiest sources of avoidable risk.",
    related: ["it-service-management-and-operational-excellence", "digital-and-technology-transformation", "benefits-realisation-and-assurance"],
  },
  {
    slug: "it-service-management-and-operational-excellence",
    name: "IT Service Management and Operational Excellence",
    short: "Improve service desk, incident, request, change, problem, knowledge, SLA and continual service improvement capability.",
    group: "optimisation",
    hero: "Improve service reliability, user experience and operational control.",
    intro: "IT services become reactive when incident volumes rise, requests are slow, knowledge is weak, change control is inconsistent and reporting does not support service improvement. Ekselens helps organisations assess ITSM maturity, identify operational pain points and build continual service improvement roadmaps.",
    challenges: [
      "Incident and request volumes are rising; root causes aren't addressed.",
      "Knowledge management is inconsistent and rarely re-used.",
      "Change failures undermine confidence in delivery.",
      "SLAs measure activity, not user experience.",
      "Problem management is reactive or absent.",
      "CSI doesn't have a backlog or an owner.",
    ],
    howWeHelp:
      "We assess ITSM where it matters — at the user. Ekselens identifies the operational pain points eroding confidence and builds a continual service improvement backlog tied to measurable outcomes.",
    delivers: [
      "ITSM maturity assessment",
      "Service desk and incident review",
      "Request fulfilment review",
      "Problem and change management review",
      "Knowledge management assessment",
      "SLA and KPI framework",
      "Continual service improvement backlog",
      "ITSM improvement roadmap",
    ],
    outputs: [
      "ITSM maturity scorecard",
      "Operational improvement backlog",
      "SLA and XLA framework",
      "Service improvement roadmap",
    ],
    whyItMatters:
      "Reliable IT services are the foundation of digital trust — and a fast route to demonstrable cost and experience improvement.",
    related: ["service-and-operating-model-transformation", "supplier-and-technology-value-optimisation", "data-insight-and-performance"],
  },
  {
    slug: "data-insight-and-performance",
    name: "Data, Insight and Performance",
    short: "Improve KPI definitions, reporting, data quality, dashboard usefulness, decision support and AI data readiness.",
    group: "optimisation",
    hero: "Turn data and reporting into better decisions.",
    intro: "Useful insight starts with clear decisions, trusted data, consistent KPI definitions and accountable ownership. Ekselens helps organisations improve data quality, reporting, dashboard usefulness, KPI governance and AI data readiness.",
    challenges: [
      "Multiple versions of the truth across reports.",
      "KPIs are defined inconsistently or not at all.",
      "Dashboards exist but don't change decisions.",
      "Data quality is unowned and unmeasured.",
      "AI ambitions are blocked by data foundations.",
      "Insight teams are stretched producing reports rather than insight.",
    ],
    howWeHelp:
      "We make insight decision-led, not report-led. Ekselens defines the decisions, the KPIs that support them, and the data foundations needed — including readiness for AI.",
    delivers: [
      "Data maturity assessment",
      "KPI dictionary",
      "Reporting and dashboard review",
      "Data quality assessment",
      "Data ownership model",
      "AI data readiness checklist",
      "Insight improvement roadmap",
    ],
    outputs: [
      "Decision-led KPI dictionary",
      "Data maturity and quality scorecard",
      "Dashboard rationalisation plan",
      "AI data readiness checklist",
    ],
    whyItMatters:
      "Boards make better decisions when they trust the data — and AI is built on the same foundation.",
    related: ["ai-readiness-and-adoption", "digital-and-technology-transformation", "benefits-realisation-and-assurance"],
  },
  {
    slug: "benefits-realisation-and-assurance",
    name: "Benefits Realisation and Assurance",
    short: "Define, track, recover and evidence measurable value from transformation, digital, AI, service and operating model change.",
    group: "delivery",
    hero: "Move from promised benefits to evidenced value.",
    intro: "Transformation outputs do not create value unless they change how the organisation works. Ekselens helps clients define, baseline, track, assure and recover benefits from digital, AI, service, operating model and supplier change.",
    challenges: [
      "Business cases promise benefits no one tracks.",
      "Benefit owners are unclear, or in name only.",
      "Baselines were never captured.",
      "Programmes finish; benefits don't.",
      "Reporting shows activity, not value delivered.",
      "Recovery actions are not triggered when benefits slip.",
    ],
    howWeHelp:
      "We make benefits real. Ekselens designs the registers, baselines, dashboards and assurance reviews that turn promised value into evidenced value — and recover it where it slips.",
    delivers: [
      "Benefits mapping workshop",
      "Benefits register",
      "Baseline and measurement approach",
      "Benefits ownership model",
      "Benefits dashboard",
      "Benefits assurance review",
      "Recovery action plan",
    ],
    outputs: [
      "Benefits map and register",
      "Baseline and measurement framework",
      "Benefits dashboard and assurance review",
      "Recovery action plan",
    ],
    whyItMatters:
      "Boards fund transformation on promised value. We make sure that value is owned, tracked and evidenced.",
    related: ["project-programme-and-pmo-improvement", "digital-and-technology-transformation", "data-insight-and-performance"],
  },
  // Three additional supporting services under "Delivery and governance" and "Adoption and capability"
  {
    slug: "governance-and-delivery-control",
    name: "Governance and Delivery Control",
    short: "Design and embed the governance, controls and assurance that protect outcomes across complex portfolios.",
    group: "delivery",
    hero: "Governance that drives decisions, not paperwork.",
    intro: "Effective governance is the invisible discipline behind credible delivery. It makes risks visible, decisions accountable and progress honest. Ekselens helps organisations design governance models that protect outcomes and earn executive trust.",
    challenges: [
      "Governance feels like overhead rather than control.",
      "Steering committees lack quorum, decisions or accountability.",
      "Risks are escalated late or not at all.",
      "Assurance is point-in-time rather than continual.",
      "Multiple bodies make overlapping decisions.",
    ],
    howWeHelp:
      "We design governance that creates clarity, not committees. Ekselens builds decision-rights, escalation paths and assurance rhythms that match the risk profile of the portfolio.",
    delivers: [
      "Governance framework and ToRs",
      "Decision rights and RACI",
      "Escalation and assurance model",
      "Risk and issue control approach",
      "Steering pack templates",
    ],
    outputs: [
      "Governance blueprint",
      "Decision rights pack",
      "Assurance rhythm calendar",
    ],
    whyItMatters: "Boards trust delivery when governance is visible, proportionate and honest.",
    related: ["project-programme-and-pmo-improvement", "benefits-realisation-and-assurance", "service-and-operating-model-transformation"],
  },
  {
    slug: "capability-and-training-needs-analysis",
    name: "Capability and Training Needs Analysis",
    short: "Identify capability gaps, design role-based learning paths and embed the skills required to sustain new ways of working.",
    group: "adoption",
    hero: "Build the capability change requires.",
    intro: "Change introduces new processes, tools and ways of working. Without targeted capability building, adoption stalls. Ekselens designs role-based capability analyses and learning plans that match change to the skills people actually need.",
    challenges: [
      "Training is generic, late or poorly attended.",
      "Capability requirements aren't mapped to roles or processes.",
      "Learning is treated as an event rather than a journey.",
      "Manager enablement is overlooked.",
      "Capability gaps surface only at go-live.",
    ],
    howWeHelp:
      "Ekselens maps capability requirements to roles, designs blended learning paths and measures uplift through proficiency, not attendance.",
    delivers: [
      "Capability requirements model",
      "Role-based training needs analysis",
      "Learning path design",
      "Manager enablement plan",
      "Proficiency assessment approach",
    ],
    outputs: [
      "Capability map and TNA",
      "Role-based learning paths",
      "Proficiency uplift dashboard",
    ],
    whyItMatters: "People sustain change when they have the capability — not just the comms.",
    related: ["business-change-and-adoption", "stakeholder-engagement-and-communications", "service-and-operating-model-transformation"],
  },
  {
    slug: "stakeholder-engagement-and-communications",
    name: "Stakeholder Engagement and Communications",
    short: "Plan and deliver structured stakeholder engagement and communications that build understanding, momentum and trust.",
    group: "adoption",
    hero: "Engagement that earns trust — not just airtime.",
    intro: "Stakeholders adopt change they have been part of shaping. Ekselens designs stakeholder engagement and communications plans that build understanding, shape feedback and sustain trust across the change journey.",
    challenges: [
      "Stakeholders feel informed at, not engaged with.",
      "Communications are one-directional and timed too late.",
      "Senior sponsors are absent from key moments.",
      "Engagement is treated as a workstream, not a discipline.",
      "Feedback loops are weak or unused.",
    ],
    howWeHelp:
      "We design engagement as a structured discipline — stakeholder maps, influence-interest analysis, two-way channels and message architectures that turn audiences into advocates.",
    delivers: [
      "Stakeholder map and analysis",
      "Engagement plan",
      "Communications strategy and channel mix",
      "Sponsor activation plan",
      "Feedback and listening approach",
    ],
    outputs: [
      "Stakeholder map and engagement plan",
      "Communications strategy",
      "Sponsor activation pack",
    ],
    whyItMatters: "Engagement done well builds the trust transformations depend on.",
    related: ["business-change-and-adoption", "capability-and-training-needs-analysis", "service-and-operating-model-transformation"],
  },
];

export const serviceGroups = [
  {
    id: "strategy",
    title: "Strategy and transformation",
    description: "Set direction, define maturity, design target models.",
    services: ["digital-and-technology-transformation", "ai-readiness-and-adoption", "service-and-operating-model-transformation"],
  },
  {
    id: "delivery",
    title: "Delivery and governance",
    description: "Make delivery confident, controlled and accountable.",
    services: ["project-programme-and-pmo-improvement", "governance-and-delivery-control", "benefits-realisation-and-assurance"],
  },
  {
    id: "adoption",
    title: "Adoption and capability",
    description: "Engage people, build capability and embed new ways of working.",
    services: ["business-change-and-adoption", "capability-and-training-needs-analysis", "stakeholder-engagement-and-communications"],
  },
  {
    id: "optimisation",
    title: "Optimisation and performance",
    description: "Optimise suppliers, services, data and operational performance.",
    services: ["supplier-and-technology-value-optimisation", "it-service-management-and-operational-excellence", "data-insight-and-performance"],
  },
];

export const priorityOffers = [
  {
    title: "AI Readiness and Adoption Roadmap",
    who: "Executive teams scaling AI beyond experimentation.",
    challenge: "AI activity is fragmented; governance, risk and value are unclear.",
    delivers: "Readiness assessment, prioritised use cases, governance model and adoption plan.",
    slug: "ai-readiness-and-adoption",
  },
  {
    title: "Digital Transformation Assessment",
    who: "Boards needing a credible, sequenced transformation plan.",
    challenge: "Digital ambition outpaces delivery readiness and benefits clarity.",
    delivers: "Maturity heatmap, opportunity portfolio, sequenced roadmap, business case.",
    slug: "digital-and-technology-transformation",
  },
  {
    title: "Project and PMO Improvement Review",
    who: "Executive sponsors who've lost confidence in delivery reporting.",
    challenge: "Reporting is optimistic; governance is administrative; benefits drift.",
    delivers: "Maturity review, governance redesign, executive dashboard, PMO improvement plan.",
    slug: "project-programme-and-pmo-improvement",
  },
  {
    title: "Supplier and Contract Optimisation Review",
    who: "CFOs and CIOs needing supplier visibility and renewal control.",
    challenge: "Contracts, renewals and SaaS licences carry hidden risk and waste.",
    delivers: "Contract inventory, renewal heatmap, optimisation opportunity register.",
    slug: "supplier-and-technology-value-optimisation",
  },
  {
    title: "ITSM Operational Excellence Review",
    who: "IT leaders facing rising incident volumes and user dissatisfaction.",
    challenge: "Service reliability and user experience are eroding executive trust.",
    delivers: "ITSM maturity scorecard, SLA/XLA framework, CSI backlog and roadmap.",
    slug: "it-service-management-and-operational-excellence",
  },
  {
    title: "Business Change and Adoption Support",
    who: "Programme directors at risk of building outputs without adoption.",
    challenge: "Communication is mistaken for change; readiness and adoption are unmeasured.",
    delivers: "Change strategy, impact pack, readiness dashboard, adoption measurement.",
    slug: "business-change-and-adoption",
  },
];

export const outcomes = [
  { title: "Clarify transformation priorities", icon: "Compass" },
  { title: "Improve delivery confidence", icon: "ShieldCheck" },
  { title: "Adopt AI responsibly", icon: "Cpu" },
  { title: "Strengthen change and adoption", icon: "Users" },
  { title: "Optimise suppliers and technology value", icon: "GitBranch" },
  { title: "Track benefits and performance", icon: "LineChart" },
];

export const differentiators = [
  { title: "Practical, not theoretical", body: "We deliver outputs clients use the day after the workshop — not frameworks that sit on a shelf." },
  { title: "Evidence-led diagnostics", body: "Every recommendation starts with diagnostics, baselines and a maturity view — not assumptions." },
  { title: "AI-ready and responsible by design", body: "Governance, oversight and adoption are designed in from day one — never bolted on." },
  { title: "Delivery and governance discipline", body: "We bring the rigour boards expect: honest reporting, real RAID, decisions that stick." },
  { title: "Adoption and people-change focus", body: "We design adoption as a discipline because that's where benefits are earned or lost." },
  { title: "Value and benefits-led", body: "Benefits are owned, tracked and assured — not promised in a business case and forgotten." },
];

export const lifecycleSteps = [
  { id: "discover", label: "Discover", note: "Clarify outcomes, scope and constraints." },
  { id: "diagnose", label: "Diagnose", note: "Maturity, baseline and pain-point analysis." },
  { id: "design", label: "Design", note: "Target state, operating model, options." },
  { id: "plan", label: "Plan", note: "Sequencing, dependencies and benefits logic." },
  { id: "mobilise", label: "Mobilise", note: "Governance, controls and team activation." },
  { id: "deliver", label: "Deliver", note: "Execution with delivery and adoption rigour." },
  { id: "embed", label: "Embed", note: "Adoption, capability and ways of working." },
  { id: "optimise", label: "Optimise", note: "Continual improvement and value tracking." },
];

// Anonymised representative engagements — illustrative of the type of work
// Ekselens undertakes. No client names, no fabricated metrics. Each entry
// follows the structure: sector / context / engagement / outcome.
// Industries we support — broad sector coverage across UK enterprise,
// public sector and regulated markets. Used on the Home page in place
// of named case studies (NDAs prevent disclosure of specific clients).
export const industries = [
  {
    name: "Financial Services",
    short: "Banking, insurance, wealth & capital markets",
    blurb:
      "Regulatory transformation, AI governance, supplier optimisation and operating-model design for tier-1 and challenger institutions.",
    icon: "Landmark",
  },
  {
    name: "Public Sector & Government",
    short: "Central, devolved and local",
    blurb:
      "Programme assurance, digital service delivery, ITSM uplift and benefits realisation aligned to GDS, NCSC and TBC standards.",
    icon: "Building2",
  },
  {
    name: "Healthcare & Life Sciences",
    short: "NHS, ICS, pharma, biotech, medtech",
    blurb:
      "Clinical-system change, data and platform modernisation, ITSM maturity and AI readiness in highly regulated environments.",
    icon: "HeartPulse",
  },
  {
    name: "Retail & E-commerce",
    short: "High-street, online & omnichannel",
    blurb:
      "Customer-platform consolidation, supplier rationalisation, peak-readiness and AI-driven operational improvement.",
    icon: "ShoppingBag",
  },
  {
    name: "Energy & Utilities",
    short: "Power, water, oil & gas, renewables",
    blurb:
      "Asset-platform transformation, field-service modernisation, SAP/Oracle programme delivery and net-zero data foundations.",
    icon: "Zap",
  },
  {
    name: "Manufacturing & Industrial",
    short: "Discrete, process and supply chain",
    blurb:
      "ERP transformation, OT/IT convergence, supplier optimisation and AI use-case prioritisation across the value chain.",
    icon: "Factory",
  },
  {
    name: "Professional Services & Legal",
    short: "Advisory, audit, law firms",
    blurb:
      "Practice-management platform change, document and knowledge management, AI-enabled productivity and risk governance.",
    icon: "Scale",
  },
  {
    name: "Telecoms & Media",
    short: "Operators, broadcasters, platforms",
    blurb:
      "Customer-platform programmes, network-IT convergence, content-supply optimisation and AI-driven service operations.",
    icon: "Radio",
  },
  {
    name: "Education",
    short: "Higher education, FE & EdTech",
    blurb:
      "Student-experience platforms, research-IT modernisation, licence and supplier optimisation, and responsible AI adoption.",
    icon: "GraduationCap",
  },
];

